'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Home, MapPin, GraduationCap, Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

interface Institution {
  id: string
  name: string
  type: string
  city: string | null
}

type SubscriptionType = 'REGION' | 'INSTITUTION'

export default function SubscribePage() {
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>('REGION')
  const [email, setEmail] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [radius, setRadius] = useState(25)
  const [institutionType, setInstitutionType] = useState('')
  const [institutionId, setInstitutionId] = useState('')
  const [graduationYear, setGraduationYear] = useState('')
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingInstitutions, setLoadingInstitutions] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  // Fetch institutions when type changes
  useEffect(() => {
    if (subscriptionType === 'INSTITUTION') {
      fetchInstitutions()
    }
  }, [subscriptionType, institutionType])

  const fetchInstitutions = async () => {
    setLoadingInstitutions(true)
    try {
      const params = new URLSearchParams()
      if (institutionType) params.set('type', institutionType)
      const res = await fetch(`/api/institutions?${params.toString()}`)
      if (res.ok) {
        const data = await res.json()
        setInstitutions(data)
      }
    } catch (err) {
      console.error('Error fetching institutions:', err)
    } finally {
      setLoadingInstitutions(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const body: Record<string, unknown> = {
        email,
        type: subscriptionType,
      }

      if (subscriptionType === 'REGION') {
        if (!city && !postalCode) {
          setError('Bitte geben Sie eine Stadt oder Postleitzahl ein')
          setLoading(false)
          return
        }
        body.city = city || null
        body.postalCode = postalCode || null
        body.radius = radius
      } else {
        if (!institutionId) {
          setError('Bitte wählen Sie eine Institution aus')
          setLoading(false)
          return
        }
        body.institutionId = institutionId
        body.graduationYear = graduationYear ? parseInt(graduationYear) : null
      }

      const res = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Ein Fehler ist aufgetreten')
        return
      }

      setSuccess(true)
    } catch (err) {
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <main className="min-h-screen bg-memorial-background">
        <nav className="bg-white border-b border-memorial-border sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link href="/" className="font-serif text-xl text-memorial-primary">
                Gedenkportal
              </Link>
              <Link
                href="/"
                className="text-memorial-text-secondary hover:text-memorial-primary transition-colors flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Startseite</span>
              </Link>
            </div>
          </div>
        </nav>

        <div className="max-w-lg mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl shadow-sm border border-memorial-border p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-serif text-2xl text-memorial-primary mb-4">
              Bestätigen Sie Ihre E-Mail
            </h1>
            <p className="text-memorial-text-secondary mb-6">
              Wir haben Ihnen eine E-Mail an <strong>{email}</strong> gesendet.
              Bitte klicken Sie auf den Link in der E-Mail, um Ihre Benachrichtigungen zu aktivieren.
            </p>
            <p className="text-sm text-memorial-text-secondary">
              Der Bestätigungslink ist 24 Stunden gültig.
            </p>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-memorial-background">
      {/* Navigation */}
      <nav className="bg-white border-b border-memorial-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="font-serif text-xl text-memorial-primary">
              Gedenkportal
            </Link>
            <Link
              href="/"
              className="text-memorial-text-secondary hover:text-memorial-primary transition-colors flex items-center gap-2"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Startseite</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-memorial-primary text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="font-serif text-3xl md:text-4xl">Benachrichtigungen abonnieren</h1>
          <p className="text-white/70 mt-2">
            Erhalten Sie täglich eine Zusammenfassung neuer Sterbefälle per E-Mail
          </p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-memorial-border p-6 md:p-8">
          {/* Type Selector */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-memorial-text-secondary mb-3">
              Worüber möchten Sie informiert werden?
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSubscriptionType('REGION')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  subscriptionType === 'REGION'
                    ? 'border-memorial-primary bg-memorial-primary/5'
                    : 'border-memorial-border hover:border-memorial-primary/50'
                }`}
              >
                <MapPin className={`w-6 h-6 mb-2 ${subscriptionType === 'REGION' ? 'text-memorial-primary' : 'text-memorial-text-secondary'}`} />
                <h3 className="font-medium text-memorial-text-primary">Region</h3>
                <p className="text-sm text-memorial-text-secondary mt-1">
                  Sterbefälle in Ihrer Stadt oder PLZ
                </p>
              </button>
              <button
                type="button"
                onClick={() => setSubscriptionType('INSTITUTION')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  subscriptionType === 'INSTITUTION'
                    ? 'border-memorial-primary bg-memorial-primary/5'
                    : 'border-memorial-border hover:border-memorial-primary/50'
                }`}
              >
                <GraduationCap className={`w-6 h-6 mb-2 ${subscriptionType === 'INSTITUTION' ? 'text-memorial-primary' : 'text-memorial-text-secondary'}`} />
                <h3 className="font-medium text-memorial-text-primary">Institution</h3>
                <p className="text-sm text-memorial-text-secondary mt-1">
                  Ehemalige Ihrer Schule, Uni etc.
                </p>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                E-Mail-Adresse *
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-memorial-text-secondary" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                />
              </div>
            </div>

            {/* Region Fields */}
            {subscriptionType === 'REGION' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                      Stadt
                    </label>
                    <input
                      type="text"
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="z.B. Berlin"
                      className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                    />
                  </div>
                  <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                      Postleitzahl
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="z.B. 10115"
                      maxLength={5}
                      className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                    />
                  </div>
                </div>
                <p className="text-sm text-memorial-text-secondary -mt-2">
                  Geben Sie Stadt oder Postleitzahl ein (oder beides)
                </p>

                <div>
                  <label htmlFor="radius" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                    Umkreis: {radius} km
                  </label>
                  <input
                    type="range"
                    id="radius"
                    value={radius}
                    onChange={(e) => setRadius(parseInt(e.target.value))}
                    min="10"
                    max="100"
                    step="5"
                    className="w-full accent-memorial-primary"
                  />
                  <div className="flex justify-between text-xs text-memorial-text-secondary mt-1">
                    <span>10 km</span>
                    <span>50 km</span>
                    <span>100 km</span>
                  </div>
                </div>
              </>
            )}

            {/* Institution Fields */}
            {subscriptionType === 'INSTITUTION' && (
              <>
                <div>
                  <label htmlFor="institutionType" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                    Art der Institution
                  </label>
                  <select
                    id="institutionType"
                    value={institutionType}
                    onChange={(e) => {
                      setInstitutionType(e.target.value)
                      setInstitutionId('')
                    }}
                    className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary bg-white"
                  >
                    <option value="">Alle Institutionen</option>
                    <option value="SCHOOL">Schule</option>
                    <option value="UNIVERSITY">Universität / Hochschule</option>
                    <option value="COMPANY">Unternehmen</option>
                    <option value="CLUB">Verein</option>
                    <option value="CHURCH">Kirchengemeinde</option>
                    <option value="MILITARY">Bundeswehr / Militär</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="institution" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                    Institution auswählen *
                  </label>
                  <select
                    id="institution"
                    value={institutionId}
                    onChange={(e) => setInstitutionId(e.target.value)}
                    required={subscriptionType === 'INSTITUTION'}
                    disabled={loadingInstitutions}
                    className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary bg-white disabled:bg-memorial-muted"
                  >
                    <option value="">
                      {loadingInstitutions ? 'Lädt...' : 'Institution auswählen'}
                    </option>
                    {institutions.map((inst) => (
                      <option key={inst.id} value={inst.id}>
                        {inst.name}{inst.city ? ` (${inst.city})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {(institutionType === 'SCHOOL' || institutionType === 'UNIVERSITY' || !institutionType) && (
                  <div>
                    <label htmlFor="graduationYear" className="block text-sm font-medium text-memorial-text-secondary mb-2">
                      Abschlussjahr (optional)
                    </label>
                    <input
                      type="number"
                      id="graduationYear"
                      value={graduationYear}
                      onChange={(e) => setGraduationYear(e.target.value)}
                      placeholder="z.B. 1985"
                      min="1900"
                      max={new Date().getFullYear()}
                      className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                    />
                    <p className="text-sm text-memorial-text-secondary mt-1">
                      Benachrichtigungen nur für Ihren Jahrgang (±3 Jahre)
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Info Box */}
            <div className="bg-memorial-muted rounded-xl p-4 flex gap-3">
              <CheckCircle className="w-5 h-5 text-memorial-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-memorial-text-secondary">
                <p className="font-medium text-memorial-text-primary mb-1">Tägliche Zusammenfassung</p>
                <p>
                  Sie erhalten einmal täglich eine E-Mail mit allen neuen Sterbefällen,
                  die Ihren Kriterien entsprechen – nur wenn es neue gibt.
                </p>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 text-red-700 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-6 rounded-xl bg-memorial-primary text-white font-medium hover:bg-memorial-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Wird gespeichert...</span>
                </>
              ) : (
                'Benachrichtigungen aktivieren'
              )}
            </button>

            <p className="text-xs text-memorial-text-secondary text-center">
              Sie können sich jederzeit mit einem Klick wieder abmelden.
            </p>
          </form>
        </div>
      </div>
    </main>
  )
}
